// The broker admin fee (the "$599") on a deal: is it there, and what should a new deal get.
//
// Henry, 2026-09-18: "Always $599" and "lets get this to work". Settings > Team Defaults has always said
// "Default admin fee ($) on each deal's commission. Overridable per deal." but nothing ever copied
// settings/teamDefaults.brokerAdminFee (camelCase) onto a deal's brokeradminfee (lowercase), and Agents cannot
// see the field, so their turn-ins saved 0. Every new-deal path and the one-time 2026 fill-in call these, so the
// app has ONE answer to "is this fee missing".
//
// Pure: no React, no Firestore. The caller passes the Team Defaults it already loaded.

export const ADMIN_FEE_FALLBACK = 599;

// A fee is present only when it reads as a number above zero. Everything else this database actually holds is
// "missing": absent, null, "" (file and listing requests), 0 (the turn-in seed), "0.00", negatives, and raw
// spreadsheet text such as "--" from the old bulk importer. A deliberate figure that is not 599 (e.g. "575") is
// present and must never be overwritten.
export function isAdminFeeMissing(raw) {
  if (raw === null || raw === undefined) return true;
  const text = String(raw).trim();
  if (text === "") return true;
  const n = Number(text);
  return !Number.isFinite(n) || n <= 0;
}

// Henry, 2026-09-18 evening: "I can also select that some 599s are waived". A 0 on its own reads as "missing"
// (above), so every fill-in would put $599 back; a waived fee is its own switch that every fee rule checks FIRST.
// Only the exact value true counts.
export function isAdminFeeWaived(deal) {
  return Boolean(deal) && deal.brokeradminfeeWaived === true;
}

// What this deal's fee really is: waived is 0; missing is the Team Defaults figure ("Always $599"); anything
// else is the figure on the deal.
export function effectiveAdminFee(deal, teamDefaults) {
  if (isAdminFeeWaived(deal)) return 0;
  const raw = deal ? deal.brokeradminfee : undefined;
  return isAdminFeeMissing(raw) ? defaultAdminFee(teamDefaults) : Number(raw);
}

// The fields a waive writes. brokeradminfee becomes "0" so the dashboard cap panel sums $0 on its own, and
// brokeradminfeeBeforeWaive remembers the figure so Undo restores it exactly ("" when there was none). Waiving a
// deal that is already waived keeps the figure remembered the first time, never the "0".
export function waiveAdminFeePatch({ deal, user, now } = {}) {
  const d = deal && typeof deal === "object" ? deal : {};
  let before;
  if (isAdminFeeWaived(d)) {
    before = isAdminFeeMissing(d.brokeradminfeeBeforeWaive) ? "" : String(Number(String(d.brokeradminfeeBeforeWaive).trim()));
  } else {
    before = isAdminFeeMissing(d.brokeradminfee) ? "" : String(Number(String(d.brokeradminfee).trim()));
  }
  const by = (user && (user.uid || user.id)) || "";
  return {
    brokeradminfee: "0",
    brokeradminfeeWaived: true,
    brokeradminfeeWaivedAt: now,
    brokeradminfeeWaivedBy: String(by),
    brokeradminfeeBeforeWaive: before,
  };
}

// Undo: the remembered figure comes back, or the Team Defaults figure when nothing was remembered. The
// waivedAt/waivedBy stamps are left as history.
export function unwaiveAdminFeePatch({ deal, teamDefaults } = {}) {
  const before = deal ? deal.brokeradminfeeBeforeWaive : undefined;
  return {
    brokeradminfee: isAdminFeeMissing(before) ? String(defaultAdminFee(teamDefaults)) : String(Number(String(before).trim())),
    brokeradminfeeWaived: false,
    brokeradminfeeBeforeWaive: "",
  };
}

// The figure a brand-new deal gets: whatever Henry keeps in Settings, or 599 when Settings has not loaded yet or
// holds something unusable.
export function defaultAdminFee(teamDefaults) {
  const raw = teamDefaults && teamDefaults.brokerAdminFee;
  return isAdminFeeMissing(raw) ? ADMIN_FEE_FALLBACK : Number(raw);
}

// A new deal's payload with its fee filled in when it has none. Stored as a digits string, which is what the
// Add Deal input itself saves ("599"), so a defaulted deal is indistinguishable from one a person typed. Returns
// a new object; the input is never mutated. Use it ONLY where a deal is being CREATED: on an edit, a 0 somebody
// chose on purpose must stay 0.
export function withDefaultAdminFee(deal, teamDefaults) {
  const base = deal && typeof deal === "object" ? deal : {};
  if (isAdminFeeWaived(base) || !isAdminFeeMissing(base.brokeradminfee)) return { ...base };
  return { ...base, brokeradminfee: String(defaultAdminFee(teamDefaults)) };
}
